// 画面ごとの document.title を中央管理する。
//
// - PAGE_TITLES: 「ルートパターン → 日本語タイトル」の対応表。
//   routes/protectedRoutes.tsx の定義順に合わせており、上から順に
//   最初にマッチしたものが使われる（静的パス /notifications/inbox を
//   動的パス /notifications/:id より先に置くこと）。
// - リダイレクト専用ルート (<Navigate> のみ) は遷移先のタイトルが
//   使われるため載せない。
// - 個別ページでより具体的なタイトル（例: イベント名）を出したい場合は、
//   ページ側で <Helmet><title> を書けばこの表より優先される。
import { matchPath } from "react-router-dom";

export const SITE_TITLE = "スマコミ管理";

interface PageTitleEntry {
  pattern: string;
  title: string;
}

// タイトルの出典: 各ページの PageHeader title を第一候補、無ければ
// サイドバーのメニュー名、それも無い詳細系はパスから命名（実画面では
// エンティティ名が出る画面。必要になればページ側 Helmet で上書きする）。
const PAGE_TITLES: ReadonlyArray<PageTitleEntry> = [
  // --- 認証済み画面 (protectedRoutes 定義順) ---
  { pattern: "/admission/code", title: "入会コード" },
  { pattern: "/admission/requests", title: "入会申請管理" },
  { pattern: "/admission/terms", title: "入会利用規約" },
  { pattern: "/analytics/management", title: "経営ダッシュボード" },
  { pattern: "/analytics/customers", title: "集客・顧客分析" },
  { pattern: "/billing/contract", title: "契約プラン" },
  { pattern: "/billing/direct-debit", title: "口座振替" },
  { pattern: "/operator/direct-debit", title: "口座振替の運営管理" },
  { pattern: "/billing/history", title: "請求一覧" },
  { pattern: "/billing/subscriptions", title: "継続契約" },
  { pattern: "/billing/pricing", title: "料金設定" },
  { pattern: "/billing/discount-rules/new", title: "月謝割引を追加" },
  {
    pattern: "/billing/discount-rules/:ruleId/edit",
    title: "月謝割引を編集",
  },
  { pattern: "/billing/discount-rules", title: "月謝割引" },
  { pattern: "/billing/discount-applications", title: "割引の適用内訳" },
  {
    pattern: "/billing/ticket-products/new",
    title: "チケット商品を追加",
  },
  {
    pattern: "/billing/ticket-products/:ticketProductId/edit",
    title: "チケット商品を編集",
  },
  { pattern: "/billing/ticket-products", title: "チケット商品" },
  { pattern: "/settings/ticketing", title: "チケット運用" },
  { pattern: "/billing/payment-setup", title: "決済サービス設定" },
  { pattern: "/operator/tenant-payments", title: "テナント決済審査" },
  { pattern: "/operator/data-reconciliation", title: "データ整理" },
  { pattern: "/operator/app-status", title: "アプリ配信管理" },
  { pattern: "/organization/branding", title: "組織情報" },
  { pattern: "/organization/payout-account", title: "振込先口座" },
  { pattern: "/organization/reschedule-policy", title: "組織の振替ルール" },
  {
    pattern: "/organization/membership-policy",
    title: "組織の会員変更ルール",
  },
  {
    pattern: "/organization/membership-policy/suspension-rules",
    title: "休会料金のルール",
  },
  {
    pattern: "/facilities/:id/membership-policy/suspension-rules",
    title: "休会料金のルール",
  },
  {
    pattern: "/organization/attendance-setting",
    title: "組織の出欠設定",
  },
  {
    pattern: "/organization/chat-support-policy",
    title: "組織のチャット受付設定",
  },
  { pattern: "/chat", title: "チャット" },
  { pattern: "/chat/:roomId", title: "チャット" },
  { pattern: "/coaches", title: "コーチ管理" },
  { pattern: "/coaches/create", title: "コーチ登録" },
  { pattern: "/courses", title: "コース管理" },
  { pattern: "/courses/create", title: "新規コース作成" },
  { pattern: "/courses/:id", title: "コース詳細" },
  { pattern: "/courses/:id/edit", title: "コース編集" },
  { pattern: "/courses/:id/sessions", title: "開講枠管理" },
  { pattern: "/courses/:id/schedule-change", title: "時間割変更" },
  { pattern: "/courses/:id/sessions/create", title: "新規開講枠を作成" },
  { pattern: "/courses/:id/sessions/:sessionId", title: "開講枠詳細" },
  {
    pattern: "/courses/:id/sessions/:sessionId/lessons/:date",
    title: "本日のレッスン",
  },
  { pattern: "/courses/:id/sessions/:sessionId/edit", title: "開講枠を編集" },
  { pattern: "/customers/members", title: "会員一覧" },
  { pattern: "/customers/members/invite", title: "会員招待" },
  {
    pattern: "/customers/members/identity-review",
    title: "同じ会員の登録を確認",
  },
  { pattern: "/customers/members/:id", title: "会員詳細" },
  { pattern: "/customers/members/:id/edit", title: "会員情報編集" },
  { pattern: "/customers/children", title: "受講者管理" },
  { pattern: "/customers/children/create", title: "受講者を登録" },
  {
    pattern: "/customers/children/identity-review",
    title: "同じ受講者の登録を確認",
  },
  { pattern: "/customers/children/:id", title: "受講者詳細" },
  { pattern: "/customers/children/:id/edit", title: "受講者を編集" },
  { pattern: "/course-enrollment-requests", title: "コース追加申請" },
  { pattern: "/membership-requests", title: "休退会申請" },
  { pattern: "/dashboard", title: "ダッシュボード" },
  { pattern: "/events", title: "イベント管理" },
  { pattern: "/events/create", title: "イベント作成" },
  { pattern: "/events/:id", title: "イベント詳細" },
  { pattern: "/events/:id/edit", title: "イベント編集" },
  { pattern: "/facilities", title: "施設・教室管理" },
  { pattern: "/facilities/create", title: "新規施設登録" },
  { pattern: "/facilities/:id", title: "施設詳細" },
  { pattern: "/facilities/:id/edit", title: "施設情報編集" },
  { pattern: "/facilities/:id/closed-dates", title: "休館日・休講日設定" },
  {
    pattern: "/facilities/:id/reschedule-policy",
    title: "施設の振替ルール",
  },
  {
    pattern: "/facilities/:id/membership-policy",
    title: "施設の会員変更ルール",
  },
  {
    pattern: "/facilities/:id/attendance-setting",
    title: "施設の出欠設定",
  },
  {
    pattern: "/facilities/:id/chat-support-policy",
    title: "施設のチャット受付設定",
  },
  { pattern: "/facilities/:id/instructors", title: "コーチ管理" },
  { pattern: "/facilities/:id/instructors/create", title: "コーチ登録" },
  {
    pattern: "/facilities/:id/instructors/:instructorId/edit",
    title: "講師編集",
  },
  { pattern: "/facilities/:id/rooms", title: "教室管理" },
  { pattern: "/facilities/:id/rooms/create", title: "新規教室登録" },
  { pattern: "/facilities/:id/rooms/:roomId/edit", title: "教室を編集" },
  { pattern: "/faqs", title: "FAQ管理" },
  { pattern: "/faqs/create", title: "FAQ を作成" },
  { pattern: "/faqs/:id/edit", title: "FAQ を編集" },
  { pattern: "/invitations", title: "招待管理" },
  { pattern: "/invitations/create", title: "新規招待作成" },
  { pattern: "/invitations/analytics", title: "招待分析ダッシュボード" },
  { pattern: "/notifications", title: "お知らせ管理" },
  { pattern: "/notifications/inbox", title: "通知一覧" },
  { pattern: "/notifications/create", title: "お知らせ作成" },
  { pattern: "/notifications/segments", title: "配信グループ管理" },
  { pattern: "/notifications/segments/create", title: "配信グループ作成" },
  { pattern: "/notifications/segments/:id", title: "配信グループ詳細" },
  {
    pattern: "/notifications/segments/:id/edit",
    title: "配信グループ編集",
  },
  // 静的パス (inbox/create/segments) より後に置くこと
  { pattern: "/notifications/:id", title: "お知らせ詳細" },
  { pattern: "/notifications/:id/edit", title: "お知らせ編集" },
  { pattern: "/attendance-reports", title: "欠席・遅刻報告" },
  { pattern: "/reschedule-requests", title: "振替管理" },
  { pattern: "/settings/account", title: "アカウント設定" },
  { pattern: "/staff", title: "スタッフ管理" },
  { pattern: "/staff/create", title: "スタッフ招待" },
  { pattern: "/staff/shifts/create", title: "新規シフト作成" },
  { pattern: "/staff/notification-settings", title: "通知・メール設定" },
  { pattern: "/staff/:id/edit", title: "スタッフ編集" },
  { pattern: "/trial-applications", title: "体験申し込み管理" },
  { pattern: "/trial-settings", title: "体験受付設定" },
  {
    pattern: "/trial-form-templates",
    title: "フォームテンプレート設定",
  },
  {
    pattern: "/trial-form-templates/create",
    title: "フォームテンプレートを作成",
  },
  {
    pattern: "/trial-form-templates/:id/edit",
    title: "フォームテンプレートを編集",
  },
  { pattern: "/products", title: "商品一覧" },
  { pattern: "/products/inventory", title: "在庫管理" },
  { pattern: "/products/new", title: "新規商品" },
  { pattern: "/products/:productId", title: "商品詳細" },
  { pattern: "/products/:productId/edit", title: "商品編集" },
  { pattern: "/surveys", title: "アンケート管理" },
  { pattern: "/surveys/create", title: "アンケート作成" },
  { pattern: "/surveys/:id", title: "アンケート詳細" },
  { pattern: "/surveys/:id/edit", title: "アンケート編集" },
  { pattern: "/inquiries", title: "お問い合わせ" },
  { pattern: "/grade-tests", title: "進級管理" },
  { pattern: "/grade-programs", title: "進級制度管理" },
  {
    pattern: "/grade-programs/:programId/ranks/:rankId",
    title: "級・グレード詳細",
  },
  {
    pattern: "/grade-programs/:programId/ranks/:rankId/edit",
    title: "級・グレードを編集",
  },
  { pattern: "/orders", title: "注文一覧" },
  { pattern: "/orders/:id", title: "注文詳細" },

  // --- 公開画面 (publicRoutes 定義順) ---
  { pattern: "/", title: "ログイン" },
  { pattern: "/verify-code", title: "メール認証" },
  { pattern: "/join", title: "入会申請" },
  { pattern: "/invite/accept", title: "会員サイトの利用登録" },
  { pattern: "/staff/invite/accept", title: "スタッフ登録" },
  { pattern: "/parent/new-password", title: "会員パスワード設定" },
  { pattern: "/new-password", title: "パスワード設定" },
  { pattern: "/account/register", title: "アカウント登録" },
  { pattern: "/account/verify-email", title: "メール認証" },
  { pattern: "/terms", title: "利用規約" },
  { pattern: "/privacy", title: "プライバシーポリシー" },
  { pattern: "/trial/:facilityId", title: "体験申し込み" },
  { pattern: "/embed/calendar/:facilityId", title: "カレンダー" },
  { pattern: "/survey/:token", title: "アンケート回答" },
  { pattern: "/events/cancel/:token", title: "イベント参加キャンセル" },
];

/** 現在のパスに対応する画面タイトルを返す。未知のパスは null */
export function resolvePageTitle(pathname: string): string | null {
  for (const entry of PAGE_TITLES) {
    if (matchPath({ path: entry.pattern, end: true }, pathname)) {
      return entry.title;
    }
  }
  return null;
}

/** document.title 用の整形。「ページ名 | スマコミ管理」形式 */
export function formatDocumentTitle(pageTitle: string | null): string {
  return pageTitle ? `${pageTitle} | ${SITE_TITLE}` : SITE_TITLE;
}
